// FILE: src/pages/ECommerceAnalytics.jsx
import React from 'react';
import Card from '../components/ui/Card';
import ChartCard from '../components/ui/ChartCard';
import DataTable from '../components/ui/DataTable';

const ECommerceAnalytics = () => {
  // KPI Data
  const kpiData = [
    {
      title: 'Total Sales',
      value: '$45,231.89',
      change: '+20.1%',
      changeType: 'increase',
      icon: '💰',
      description: 'from last month'
    },
    {
      title: 'Total Revenue',
      value: '$12,234.67',
      change: '+15.3%',
      changeType: 'increase',
      icon: '📈',
      description: 'from last month'
    },
    {
      title: 'Orders',
      value: '1,234',
      change: '+8.2%',
      changeType: 'increase',
      icon: '🛒',
      description: 'from last month'
    },
    {
      title: 'Conversion Rate',
      value: '3.24%',
      change: '-2.1%',
      changeType: 'decrease',
      icon: '🎯',
      description: 'from last month'
    },
    {
      title: 'Avg. Order Value',
      value: '$89.67',
      change: '+12.5%',
      changeType: 'increase',
      icon: '💳',
      description: 'from last month'
    }
  ];

  // Top Products Data
  const topProductsData = [
    { 
      id: 1, 
      name: 'Wireless Headphones', 
      category: 'Electronics', 
      sales: 1234, 
      revenue: '$45,678', 
      status: 'In Stock',
      image: '🎧'
    },
    { 
      id: 2, 
      name: 'Smart Watch', 
      category: 'Electronics', 
      sales: 987, 
      revenue: '$34,567', 
      status: 'Low Stock',
      image: '⌚'
    },
    { 
      id: 3, 
      name: 'Running Shoes', 
      category: 'Sports', 
      sales: 856, 
      revenue: '$28,901', 
      status: 'In Stock',
      image: '👟'
    },
    { 
      id: 4, 
      name: 'Coffee Maker', 
      category: 'Home', 
      sales: 723, 
      revenue: '$21,234', 
      status: 'Out of Stock',
      image: '☕'
    },
    { 
      id: 5, 
      name: 'Laptop Bag', 
      category: 'Accessories', 
      sales: 654, 
      revenue: '$18,567', 
      status: 'In Stock',
      image: '💼'
    }
  ];

  // Geographic Data
  const geoData = [
    { country: 'United States', sales: '$15,234', percentage: '34.2%', flag: '🇺🇸' },
    { country: 'United Kingdom', sales: '$8,567', percentage: '19.3%', flag: '🇬🇧' },
    { country: 'Germany', sales: '$6,789', percentage: '15.2%', flag: '🇩🇪' },
    { country: 'France', sales: '$4,321', percentage: '9.7%', flag: '🇫🇷' },
    { country: 'Canada', sales: '$3,456', percentage: '7.8%', flag: '🇨🇦' },
    { country: 'Others', sales: '$6,234', percentage: '13.8%', flag: '🌍' }
  ];

  const productColumns = [
    { 
      key: 'image', 
      label: '', 
      render: (value) => <span className="text-2xl">{value}</span>,
      sortable: false 
    },
    { key: 'name', label: 'Product Name' },
    { key: 'category', label: 'Category' },
    { key: 'sales', label: 'Sales' },
    { key: 'revenue', label: 'Revenue' },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          value === 'In Stock' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
          value === 'Low Stock' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
        }`}>
          {value}
        </span>
      )
    }
  ];

  const geoColumns = [
    { 
      key: 'flag', 
      label: '', 
      render: (value) => <span className="text-xl">{value}</span>,
      sortable: false 
    },
    { key: 'country', label: 'Country' },
    { key: 'sales', label: 'Sales' },
    { key: 'percentage', label: 'Percentage' }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">E-Commerce Analytics</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Monitor your sales performance and key metrics</p>
        </div>
        <div className="flex space-x-3">
          <button className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Export
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            View Report
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {kpiData.map((kpi, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{kpi.icon}</span>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{kpi.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{kpi.value}</p>
                <div className="flex items-center mt-1">
                  <span className={`text-sm font-medium ${
                    kpi.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {kpi.change}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">{kpi.description}</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales vs Revenue Chart */}
        <ChartCard 
          title="Sales vs Revenue" 
          subtitle="Monthly comparison"
          chartType="line"
          data={[
            { name: 'Jan', sales: 45000, revenue: 38000 },
            { name: 'Feb', sales: 52000, revenue: 44000 },
            { name: 'Mar', sales: 48000, revenue: 41000 },
            { name: 'Apr', sales: 61000, revenue: 52000 },
            { name: 'May', sales: 55000, revenue: 47000 },
            { name: 'Jun', sales: 67000, revenue: 58000 },
            { name: 'Jul', sales: 72000, revenue: 62000 },
            { name: 'Aug', sales: 68000, revenue: 59000 },
            { name: 'Sep', sales: 74000, revenue: 64000 },
            { name: 'Oct', sales: 81000, revenue: 70000 },
            { name: 'Nov', sales: 76000, revenue: 66000 },
            { name: 'Dec', sales: 89000, revenue: 78000 }
          ]}
          dataKey={['sales', 'revenue']}
          xAxisKey="name"
          colors={['#3B82F6', '#10B981']}
          className="lg:col-span-2"
        />
        
        {/* Product Categories Chart */}
        <ChartCard 
          title="Sales by Category" 
          subtitle="Product category performance"
          chartType="bar"
          data={[
            { name: 'Electronics', value: 156000 },
            { name: 'Clothing', value: 98000 },
            { name: 'Home & Garden', value: 87000 },
            { name: 'Sports', value: 76000 },
            { name: 'Books', value: 45000 },
            { name: 'Toys', value: 38000 }
          ]}
          dataKey="value"
          xAxisKey="name"
          colors={['#8B5CF6']}
        />
        
        {/* Top Regions Chart */}
        <ChartCard 
          title="Revenue by Region" 
          subtitle="Geographic distribution"
          chartType="pie"
          data={[
            { name: 'North America', value: 234000 },
            { name: 'Europe', value: 189000 },
            { name: 'Asia Pacific', value: 156000 },
            { name: 'Latin America', value: 78000 },
            { name: 'Middle East', value: 45000 },
            { name: 'Africa', value: 23000 }
          ]}
          dataKey="value"
          colors={['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6B7280']}
        />
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Products Table */}
        <div className="lg:col-span-2">
          <Card title="Top Products" subtitle="Best performing products this month">
            <DataTable 
              columns={productColumns}
              data={topProductsData}
              searchable={true}
              pagination={false}
            />
          </Card>
        </div>

        {/* Geographic Sales */}
        <Card title="Sales by Region" subtitle="Geographic performance breakdown">
          <DataTable 
            columns={geoColumns}
            data={geoData}
            searchable={false}
            pagination={false}
          />
        </Card>
      </div>
    </div>
  );
};

export default ECommerceAnalytics;